import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  collection,
  getDocs,
  orderBy,
  query,
  Timestamp,
  where,
} from 'firebase/firestore';
import {
  CalendarCheck,
  CalendarPlus,
  LayoutDashboard,
  Megaphone,
  RefreshCw,
  Users,
} from 'lucide-react';
import { db } from '../../firebase';
import { useUser } from '../../contexts/UserContext';
import { WorkshopV2 } from '../../interfaces/workshop.interface';
import { Participant } from '../../interfaces/reservation.interface';
import { GiftcardListItem } from '../../interfaces/giftcard.interface';
import {
  giftcardErrorMessage,
  listGiftcards,
} from '../../services/giftcards.service';
import { fetchParticipantsByWorkshop } from '../../utils/reservation';
import StatTile, { SkeletonTile } from '../../components/StatTile';
import { ParticipantsState } from '../history/components/HistoryStats';
import AlertBand from './components/AlertBand';
import GiftcardPanel from './components/GiftcardPanel';
import TodayPanel from './components/TodayPanel';
import UpcomingFillPanel from './components/UpcomingFillPanel';
import {
  buildAlerts,
  buildUpcomingRows,
  capacityOf,
  countUnactionableUpcoming,
  dayAndMonth,
  duoCount,
  fillRate,
  GIFTCARD_FETCH_LIMIT,
  headcountOf,
  isOpen,
  nextWorkshop as findNextWorkshop,
  normalizeWorkshop,
  numberFormat,
  openUpcoming,
  placesOf,
  rowsFor,
  SELL_HORIZON_DAYS,
  startOfToday,
  unknownTypeCount,
  workshopsToday,
} from './utils/dashboardData';

/**
 * Tableau de bord — page d'accueil, strictement en LECTURE SEULE.
 *
 * Deux lectures Firestore seulement : une requête `workshops_v2` bornée par une
 * plage sur `date` (deux inégalités sur le champ trié : aucun index composite),
 * puis les réservations de ces ateliers via `fetchParticipantsByWorkshop`. Les
 * filtres secondaires (actif, ouvert, type) se font en mémoire — les combiner à
 * la plage de dates exigerait un index composite qui n'existe pas.
 *
 * Les bons cadeaux vivent dans un effet séparé : la callable est rarement
 * sollicitée, son démarrage à froid figerait toute la page si elle était
 * attendue avec le reste.
 */

/** Profondeur annoncée dans le sous-titre et couverte par la requête. */
const HORIZON_DAYS = 30;

const DashboardPage = () => {
  const navigate = useNavigate();
  const { user, loading: userLoading } = useUser();
  const isAdmin = !userLoading && user?.role === 'admin';

  const [workshops, setWorkshops] = useState<WorkshopV2[]>([]);
  const [participants, setParticipants] = useState<
    Record<string, Participant[]>
  >({});

  const [loading, setLoading] = useState(true);
  const [participantsLoading, setParticipantsLoading] = useState(false);
  /** Échec fatal de la requête ateliers : le corps de la page est remplacé. */
  const [error, setError] = useState<string | null>(null);
  /** Échec des réservations : dégradation, les ateliers restent affichés. */
  const [participantsError, setParticipantsError] = useState<string | null>(
    null
  );

  /**
   * Instant de référence, figé au chargement. Toutes les dérivations le
   * partagent : un `new Date()` par calcul les ferait diverger d'un memo à
   * l'autre (un atelier « ouvert » ici et « fermé » deux lignes plus bas).
   */
  const [now, setNow] = useState<Date>(() => new Date());

  const [giftcards, setGiftcards] = useState<GiftcardListItem[]>([]);
  /**
   * Démarre à `true` : le panneau est monté par le rendu qui découvre le rôle
   * admin, alors que l'effet qui lance l'appel ne s'exécute qu'après la peinture
   * de ce rendu. À `false`, la première image affichée serait « 0 bon en
   * circulation », remplacée un instant plus tard par le squelette.
   */
  const [giftcardsLoading, setGiftcardsLoading] = useState(true);
  const [giftcardsError, setGiftcardsError] = useState<string | null>(null);
  const [giftcardsToken, setGiftcardsToken] = useState(0);

  /** Invalide les réponses des chargements abandonnés (rafraîchissements groupés). */
  const requestId = useRef(0);

  const load = useCallback(async () => {
    const id = ++requestId.current;
    const reference = new Date();

    setLoading(true);
    setError(null);
    setParticipantsError(null);
    // `true` dès maintenant, et pas seulement une fois les ateliers reçus : la
    // table `participants` est vide (ou périmée) pendant toute la requête
    // ateliers. À `false`, l'état passerait pour « prêt » et les panneaux
    // présenteraient ces zéros comme des réservations réelles.
    setParticipantsLoading(true);
    setNow(reference);

    // Depuis ce matin 00h00 : la journée en cours doit rester entière pour la
    // feuille d'émargement, mais plus rien n'a besoin des ateliers passés.
    const from = startOfToday(reference);
    const to = startOfToday(reference);
    to.setDate(to.getDate() + HORIZON_DAYS + 1); // borne exclusive

    try {
      const snapshot = await getDocs(
        query(
          collection(db, 'workshops_v2'),
          where('date', '>=', Timestamp.fromDate(from)),
          where('date', '<', Timestamp.fromDate(to)),
          orderBy('date', 'asc')
        )
      );
      if (id !== requestId.current) return;

      // Pas de `limit()` : la fenêtre est structurellement petite, et un plafond
      // non annoncé ferait silencieusement sous-compter les trois tuiles.
      const items = snapshot.docs
        .map((doc) => normalizeWorkshop({ ...doc.data(), id: doc.id } as WorkshopV2))
        .filter((workshop): workshop is WorkshopV2 => workshop !== null);

      setWorkshops(items);
      setParticipants({});
      setLoading(false);

      if (items.length === 0) {
        setParticipantsLoading(false);
        return;
      }

      // Les ateliers s'affichent immédiatement, les réservations complètent après.
      try {
        const map = await fetchParticipantsByWorkshop(
          items.map((workshop) => workshop.id)
        );
        if (id !== requestId.current) return;
        setParticipants(map);
      } catch (err) {
        console.error('Erreur chargement des réservations :', err);
        if (id !== requestId.current) return;
        setParticipantsError(
          String(err).includes('Missing or insufficient permissions')
            ? 'Accès refusé aux réservations. Vérifiez vos règles Firestore / rôle admin.'
            : 'Les réservations n’ont pas pu être chargées.'
        );
      } finally {
        if (id === requestId.current) setParticipantsLoading(false);
      }
    } catch (err) {
      console.error('Erreur lors du chargement du tableau de bord :', err);
      if (id !== requestId.current) return;
      setError('Impossible de charger les données du tableau de bord.');
      setLoading(false);
      setParticipantsLoading(false);
      // Sans ça, les tuiles afficheraient les totaux du chargement précédent
      // juste au-dessus du message d'erreur.
      setWorkshops([]);
      setParticipants({});
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * Bons cadeaux : effet séparé, et on échoue FERMÉ. UserContext démarre à
   * `loading: true` et met `user` à null si la lecture échoue — tant que le rôle
   * n'est pas connu, aucun appel. Appeler puis rattraper l'erreur coûterait une
   * invocation et une lecture Firestore avant le refus, plus un rouge en console
   * à chaque chargement pour un compte non-admin.
   */
  useEffect(() => {
    if (!isAdmin) return;
    let alive = true;

    setGiftcardsLoading(true);
    setGiftcardsError(null);
    listGiftcards({ limit: GIFTCARD_FETCH_LIMIT })
      .then((rows) => {
        if (alive) setGiftcards(rows);
      })
      .catch((err) => {
        console.error('Erreur chargement des bons cadeaux :', err);
        if (alive) setGiftcardsError(giftcardErrorMessage(err));
      })
      .finally(() => {
        if (alive) setGiftcardsLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [isAdmin, giftcardsToken]);

  const handleRefresh = () => {
    load();
    setGiftcardsToken((token) => token + 1);
  };

  const participantsState: ParticipantsState = participantsError
    ? 'unavailable'
    : participantsLoading
      ? 'loading'
      : 'ready';
  const ready = participantsState === 'ready';
  const pendingHint =
    participantsState === 'loading'
      ? 'chargement…'
      : 'réservations indisponibles';

  const today = useMemo(
    () => workshopsToday(workshops, now),
    [workshops, now]
  );

  /** Ateliers ouverts dont l'échéance tombe dans la semaine. */
  const sellSoon = useMemo(() => {
    const horizon = now.getTime() + SELL_HORIZON_DAYS * 24 * 60 * 60 * 1000;
    return openUpcoming(workshops, now).filter(
      (workshop) => workshop.date.toDate().getTime() <= horizon
    );
  }, [workshops, now]);

  const upcoming = useMemo(
    () =>
      workshops.filter(
        (workshop) => workshop.date.toDate().getTime() > now.getTime()
      ),
    [workshops, now]
  );
  const openCount = useMemo(
    () => upcoming.filter((workshop) => isOpen(workshop, now)).length,
    [upcoming, now]
  );

  const alerts = useMemo(
    () => buildAlerts(workshops, participants, now),
    [workshops, participants, now]
  );

  const upcomingRows = useMemo(
    () => buildUpcomingRows(workshops, participants, now, ready),
    [workshops, participants, now, ready]
  );
  const excludedCount = useMemo(
    () => countUnactionableUpcoming(workshops, now),
    [workshops, now]
  );

  // --- Tuile 1 : personnes attendues aujourd'hui
  const todayRows = rowsFor(today, participants);
  const headcount = headcountOf(todayRows);
  const todayUnknown = unknownTypeCount(todayRows);
  const todayDuos = duoCount(todayRows);
  const noWorkshopToday = today.length === 0;

  const headcountHint = noWorkshopToday
    ? "pas d'atelier aujourd'hui"
    : !ready
      ? pendingHint
      : `${placesOf(todayRows)} place${placesOf(todayRows) > 1 ? 's' : ''} occupée${
          placesOf(todayRows) > 1 ? 's' : ''
        } · ${todayDuos} duo${todayDuos > 1 ? 's' : ''}${
          todayUnknown > 0
            ? ` · ${todayUnknown} sans formule renseignée, compté${
                todayUnknown > 1 ? 's' : ''
              } pour 1`
            : ''
        }`;

  // --- Tuile 2 : places encore à vendre sur la semaine
  const sellRows = rowsFor(sellSoon, participants);
  const sellCapacity = capacityOf(sellSoon);
  const sellPlaces = placesOf(sellRows);
  const sellFree = Math.max(0, sellCapacity - sellPlaces);
  const noOpenThisWeek = sellSoon.length === 0;

  // --- Tuile 3 : ateliers ouverts sur la fenêtre
  const closedUpcoming = upcoming.length - openCount;

  const windowLabel = useMemo(() => {
    const start = startOfToday(now);
    const end = startOfToday(now);
    end.setDate(end.getDate() + HORIZON_DAYS);
    return `${dayAndMonth(start)} au ${dayAndMonth(end)}`;
  }, [now]);

  return (
    <div className="h-full flex flex-col p-6 gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold text-[#623413]">
            <LayoutDashboard size={22} />
            Tableau de bord
          </h1>
          <p className="text-sm text-gray-500">
            Ateliers programmés du {windowLabel}.
          </p>
        </div>

        <button
          type="button"
          onClick={handleRefresh}
          className="rounded-lg p-2 text-gray-500 transition hover:bg-gray-100"
          title="Actualiser"
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Rien tant que les réservations ne sont pas là : une alerte « aucune
          réservation » fabriquée pendant le chargement est pire que pas d'alerte. */}
      {!loading && !error && ready && <AlertBand alerts={alerts} />}

      {participantsError && (
        <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-700">
          {participantsError}
        </p>
      )}

      {/* Toujours trois tuiles : un nombre variable recalculerait toutes les
          largeurs `flex-1` d'un chargement à l'autre et se lirait comme un bug. */}
      {!error && (
        <div className="flex flex-wrap gap-3">
          {loading ? (
            [...Array(3)].map((_, i) => <SkeletonTile key={i} />)
          ) : (
            <>
              <StatTile
                icon={<Users size={14} />}
                label="Personnes aujourd'hui"
                value={
                  noWorkshopToday || !ready
                    ? '—'
                    : numberFormat.format(headcount)
                }
                hint={headcountHint}
              />
              <StatTile
                icon={<Megaphone size={14} />}
                label={`Places à vendre — ${SELL_HORIZON_DAYS} jours`}
                value={
                  noOpenThisWeek || !ready ? '—' : numberFormat.format(sellFree)
                }
                meter={
                  noOpenThisWeek || !ready
                    ? undefined
                    : fillRate(sellPlaces, sellCapacity)
                }
                hint={
                  noOpenThisWeek
                    ? 'aucun atelier ouvert cette semaine'
                    : !ready
                      ? pendingHint
                      : `${sellPlaces} / ${sellCapacity} places vendues sur ${
                          sellSoon.length
                        } atelier${sellSoon.length > 1 ? 's' : ''} ouvert${
                          sellSoon.length > 1 ? 's' : ''
                        }`
                }
              />
              <StatTile
                icon={<CalendarCheck size={14} />}
                label={`Ateliers ouverts — ${HORIZON_DAYS} jours`}
                // Ici un 0 est une vraie information : aucune donnée manquante
                // n'entre dans ce calcul, il ne dépend pas des réservations.
                value={numberFormat.format(openCount)}
                hint={`sur ${upcoming.length} programmé${
                  upcoming.length > 1 ? 's' : ''
                }${
                  closedUpcoming > 0
                    ? ` · ${closedUpcoming} inactif${
                        closedUpcoming > 1 ? 's' : ''
                      } ou fermé${closedUpcoming > 1 ? 's' : ''}`
                    : ''
                }`}
              />
            </>
          )}
        </div>
      )}

      <div className="flex-1 overflow-y-auto flex flex-col gap-4 pr-2">
        {error ? (
          <div className="flex flex-col items-center gap-3 py-10">
            <p className="text-center text-sm text-[#BB0F33]">{error}</p>
            <button
              type="button"
              onClick={handleRefresh}
              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 transition hover:bg-gray-50"
            >
              <RefreshCw size={16} />
              Réessayer
            </button>
          </div>
        ) : !loading && workshops.length === 0 ? (
          <div className="h-full min-h-[420px] flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-gray-200 bg-white/50 p-8 text-center">
            <CalendarPlus size={40} className="text-[#623413]/30" />
            <p className="mt-4 text-lg font-semibold text-gray-700">
              Aucun atelier programmé.
            </p>
            <p className="mt-2 max-w-sm text-sm text-gray-500">
              Rien n'est prévu dans les {HORIZON_DAYS} prochains jours.
            </p>
            <button
              type="button"
              onClick={() => navigate('/workshops')}
              className="mt-4 text-[#623413] hover:underline"
            >
              Créer un atelier
            </button>
          </div>
        ) : (
          <>
            <TodayPanel
              workshops={today}
              participants={participants}
              participantsState={participantsState}
              participantsError={participantsError}
              nextWorkshop={findNextWorkshop(workshops, now)}
              loading={loading}
            />

            <div className="grid gap-4 lg:grid-cols-3">
              <UpcomingFillPanel
                rows={upcomingRows}
                excludedCount={excludedCount}
                participantsState={participantsState}
                loading={loading}
                className={isAdmin ? 'lg:col-span-2' : 'lg:col-span-3'}
              />

              {/* Non monté pour un non-admin : une page d'accueil n'accueille
                  pas quelqu'un par un refus d'accès. La grille se reflow. */}
              {isAdmin && (
                <GiftcardPanel
                  items={giftcards}
                  loading={giftcardsLoading}
                  error={giftcardsError}
                />
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DashboardPage;
